import React from 'react';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import GlobalStyles from '@/components/Styles';
import DefaultLayout from '@/components/Layout';
import { DefaultSeo } from 'next-seo';
import Rum from '@/components/Rum';
import ErrorBoundary from '@/components/errors/ErrorBoundary';
import { AmplitudeProvider } from '@/utils/hooks/useAmplitude';
import getConfig from 'next/config';
import { SEO } from '../constants/index';

const { publicRuntimeConfig } = getConfig();

if (publicRuntimeConfig.API_MOCKING === 'enabled') {
  // eslint-disable-next-line global-require
  require('../mocks');
}

export default function MyApp({ Component, pageProps }: { Component: any, pageProps: any }) {
  const [queryClient] = React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 100000,
      },
    },
  }));

  const Layout = Component.getLayout || DefaultLayout;
  const titleTemplate = `%s | ${SEO.SITE_NAME}`;
  return (
    <QueryClientProvider client={queryClient}>
      <Hydrate state={pageProps.dehydratedState}>
        <GlobalStyles />
        <Rum />
        <AmplitudeProvider>
          <Layout {...pageProps}>
            <DefaultSeo
              titleTemplate={titleTemplate}
              dangerouslySetAllPagesToNoIndex={!publicRuntimeConfig.SEARCH_ENGINES}
              dangerouslySetAllPagesToNoFollow={!publicRuntimeConfig.SEARCH_ENGINES}
              openGraph={{
                type: 'website',
                locale: 'en_US',
                url: SEO.FANDUEL_URL_RESEARCH,
                siteName: SEO.SITE_NAME,
              }}
              twitter={{
                handle: SEO.TWITTERHANDLE,
                site: SEO.SITE_NAME,
                cardType: 'summary_large_image',
              }}
            />
            <ErrorBoundary>
              <Component {...pageProps} />
            </ErrorBoundary>
          </Layout>
        </AmplitudeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </Hydrate>
    </QueryClientProvider>
  );
}
